import { useEffect, useState } from 'react';
import { Table, SimpleGrid, Text, Stack, Paper, Group, Button } from '@mantine/core';
import { IconAdd as IconPlus, IconCaliper } from '../../components/icons/CncIcons';
import EntityHeader from '../../components/EntityHeader';
import { ViewMode } from '../../components/TableGridToggle';
import { api, imageUrl } from '../../api/client';
import AccessoryModal from './AccessoryModal';

export interface Accessory {
  id: number;
  name: string;
  manufacturer: string | null;
  model: string | null;
  imagePath: string | null;
  powerKwh: number | null;
  valueEur: number | null;
  serviceLifeHours: number | null;
  markupPct: number;
  depreciationPerHour: number | null;
  hourlyRateEur: number | null;
}

export default function AccessoriesPage() {
  const [view, setView] = useState<ViewMode>('table');
  const [rows, setRows] = useState<Accessory[]>([]);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);

  async function refresh() {
    setRows(await api.get<Accessory[]>('/accessories'));
  }
  useEffect(() => { refresh(); }, []);

  return (
    <Stack>
      <EntityHeader title="Lab Accessories" icon={<IconCaliper size={26} />} view={view} onView={setView} />

      {view === 'table' ? (
        <Paper withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 80 }}>Image</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th style={{ width: 140 }}>Manufacturer</Table.Th>
                <Table.Th style={{ width: 140 }}>Model</Table.Th>
                <Table.Th style={{ width: 120 }}>Cost (EUR)</Table.Th>
                <Table.Th style={{ width: 100 }}>Markup</Table.Th>
                <Table.Th style={{ width: 120 }}>Price (EUR)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => {
                const cost = ((r.powerKwh ?? 0) + (r.depreciationPerHour ?? 0));
                return (
                  <Table.Tr key={r.id} className="click-row" onClick={() => setEditingId(r.id)}>
                    <Table.Td>
                      <div className="image-box">
                        {r.imagePath && <img src={imageUrl(r.imagePath, true)} alt={r.name} />}
                      </div>
                    </Table.Td>
                    <Table.Td>{r.name}</Table.Td>
                    <Table.Td>{r.manufacturer ?? '—'}</Table.Td>
                    <Table.Td>{r.model ?? '—'}</Table.Td>
                    <Table.Td>{cost > 0 ? cost.toFixed(2) : '—'}</Table.Td>
                    <Table.Td>{r.markupPct}%</Table.Td>
                    <Table.Td>{r.hourlyRateEur != null ? r.hourlyRateEur.toFixed(2) : '—'}</Table.Td>
                  </Table.Tr>
                );
              })}
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditingId('new')} data-tour="accessories-add">
                    Add Accessory
                  </Button>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }}>
          {rows.map((r) => (
            <div key={r.id} className="grid-card" onClick={() => setEditingId(r.id)}>
              <div className="card-image">
                {r.imagePath ? <img src={imageUrl(r.imagePath)} alt={r.name} /> : <Text c="dimmed" size="xs">No image</Text>}
              </div>
              <Group justify="space-between">
                <Text fw={600}>{r.name}</Text>
                <Text size="sm" c="orange">{r.hourlyRateEur != null ? `€${r.hourlyRateEur.toFixed(2)}/h` : '—'}</Text>
              </Group>
            </div>
          ))}
          <div className="add-card" onClick={() => setEditingId('new')}>
            <Group gap={6}><IconPlus size={16} /><Text>Add Accessory</Text></Group>
          </div>
        </SimpleGrid>
      )}

      {editingId !== null && (
        <AccessoryModal
          id={editingId === 'new' ? null : editingId}
          onClose={() => setEditingId(null)}
          onSaved={async () => { await refresh(); setEditingId(null); }}
          onDeleted={async () => { await refresh(); setEditingId(null); }}
        />
      )}
    </Stack>
  );
}
